// "Das wird angelegt" ueber der Aktionszeile der Schnellerfassung: steht nur,
// wenn mit dem Kontakt eine Organisation verknuepft oder mitangelegt wird.
// Ein einzelner Kontakt braucht keine Zusammenfassung (5.2 Nr. 14).
//
// KEINE LIVE-REGION und kein Tabstopp: die Vorschau aendert sich beim Tippen
// und darf nicht bei jedem Zeichen angesagt werden. Wer mit Bildschirmleser
// arbeitet, liest sie mit dem Lesecursor direkt vor dem Knopf (NFR-ACC-001).

import { useId, type ReactElement } from 'react';
import { Badge, Body1, Caption1, makeStyles, tokens } from '@fluentui/react-components';

import { LEITUNG_VORSCHAU_ZEILE, leitungVorschauVerbindung } from '../leitung.ts';
import { PREVIEW_HEADING, PREVIEW_NO_NAME, PREVIEW_ORGANIZATION_LINE, PREVIEW_WORKS_FOR, previewPersonLine } from '../quick-capture.ts';

const useStyles = makeStyles({
  vorschau: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    padding: '0.75rem',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflowWrap: 'anywhere',
  },
  titel: { margin: 0, fontSize: 'inherit' },
  zeile: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.25rem 0.5rem', minWidth: 0 },
  gedaempft: { color: tokens.colorNeutralForeground2 },
  verbindung: { paddingInlineStart: '1rem', color: tokens.colorNeutralForeground2 },
  // Angaben der Organisation brechen an Wortgrenzen um, nicht mitten im Wort (Vorgabe S2, Lehre aus #725).
  angabe: { color: tokens.colorNeutralForeground2, overflowWrap: 'break-word' },
});

export function AnlegenVorschau({
  name,
  ersterKontaktweg,
  funktion,
  organisation,
  angaben = [],
  leitung = [],
}: {
  readonly name: string;
  readonly ersterKontaktweg: string | undefined;
  /** Die Funktion an "Arbeitet für" in Worten (S1b), sonst undefined. */
  readonly funktion: string | undefined;
  readonly organisation: { readonly name: string; readonly neu: boolean };
  /** Die Angaben der neuen Organisation, je eine Zeile (S2); nur bei `organisation.neu`. */
  readonly angaben?: readonly string[];
  /** Die gewaehlten Personen der Leitung (S3, V7); auch an der eben angelegten Organisation nach dem Teilerfolg. */
  readonly leitung?: readonly string[];
}): ReactElement {
  const styles = useStyles();
  const titelId = useId();
  const anzeigename = name.trim();
  return (
    <section className={styles.vorschau} aria-labelledby={titelId}>
      <Body1 as="h2" id={titelId} className={styles.titel}>
        <strong>{PREVIEW_HEADING}</strong>
      </Body1>
      <span className={styles.zeile}>
        <Body1 className={anzeigename === '' ? styles.gedaempft : undefined}>{anzeigename === '' ? PREVIEW_NO_NAME : anzeigename}</Body1>
        {/* Dieselben Marken wie in der Karte unter "Arbeitet für"; in Worten, nicht Farbe allein. */}
        <Badge appearance="outline" color="brand">
          Neu
        </Badge>
      </span>
      <Caption1 className={styles.gedaempft}>{previewPersonLine(ersterKontaktweg, funktion)}</Caption1>
      <Caption1 className={styles.verbindung}>{PREVIEW_WORKS_FOR}</Caption1>
      <span className={styles.zeile}>
        <Body1>{organisation.name}</Body1>
        <Badge appearance="outline" color={organisation.neu ? 'brand' : 'informative'}>
          {organisation.neu ? 'Neu' : 'Vorhanden'}
        </Badge>
      </span>
      {organisation.neu ? <Caption1 className={styles.gedaempft}>{PREVIEW_ORGANIZATION_LINE}</Caption1> : undefined}
      {organisation.neu
        ? angaben.map((zeile) => (
            <Caption1 key={zeile} className={styles.angabe}>
              {zeile}
            </Caption1>
          ))
        : undefined}
      {leitung.length > 0 ? (
        <>
          <Caption1 className={styles.verbindung}>{leitungVorschauVerbindung(organisation.name)}</Caption1>
          {leitung.map((name) => (
            <span key={name} className={styles.zeile}>
              <Body1>{name}</Body1>
              <Badge appearance="outline" color="brand">
                Neu
              </Badge>
            </span>
          ))}
          {/* Einmal fuer alle: sie entstehen gleich, nur mit Namen (V7). */}
          <Caption1 className={styles.gedaempft}>{LEITUNG_VORSCHAU_ZEILE}</Caption1>
        </>
      ) : undefined}
    </section>
  );
}
